import { FC } from "react";

import { Locales, RecaptchaGuardMessage } from "@simplyk/common";

import { BuyerLoginDialog } from "@/canvas/features/FormV2/blocks/BuyerLoginDialog/BuyerLoginDialog";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { trpc } from "@/common/helpers/trpc";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { useTranslate } from "@/common/hooks/useTranslate";

interface MemberLoginDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  ticketingId: string;
  locale: Locales;
}

export const MemberLoginDialog: FC<MemberLoginDialogProps> = ({
  open,
  onClose,
  organizationId,
  ticketingId,
  locale,
}) => {
  const { t } = useTranslate();
  const { executeTrpc } = useRecaptchaRetry();
  const { mutateAsync: sendMemberLoginLink, isPending } = trpc.form_sendMemberLoginLink.useMutation();

  const handleSendLink = async (email: string) => {
    const { error } = await executeTrpc(RecaptchaAction.SendMemberLoginLink, email, sendMemberLoginLink, {
      email,
      organizationId,
      ticketingId,
      locale,
    });

    if (!error) {
      return null;
    }

    return error.code === RecaptchaGuardMessage
      ? t("common", "error.recaptchaError")
      : t("common", "tryAgainOrContactUs");
  };

  return (
    <BuyerLoginDialog
      open={open}
      onClose={onClose}
      onSendLink={handleSendLink}
      isPending={isPending}
      dataTestPrefix="member-login-dialog"
      copy={{
        title: t("common", "memberLoginDialog.title"),
        description: t("common", "memberLoginDialog.body"),
        emailLabel: t("common", "memberLoginDialog.emailLabel"),
        emailPlaceholder: t("common", "memberLoginDialog.emailPlaceholder"),
        sendLabel: t("common", "memberLoginDialog.sendLoginLink"),
        cancelLabel: t("common", "memberLoginDialog.cancel"),
        closeLabel: t("common", "close"),
        successTitle: t("common", "memberLoginDialog.successTitle"),
        successDescription: t("common", "memberLoginDialog.successBody"),
        invalidEmailMessage: t("common", "emailFormat"),
      }}
    />
  );
};
